import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Lock,
  CheckCircle,
} from "@mui/icons-material";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/EnterpriseAuthContext";
import { AuthLayout, AuthCard } from "./pagekit";

function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const {
    actions: { requirePasswordReset, clearPasswordResetRequirement },
  } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [formErrors, setFormErrors] = useState({});

  // Check if we have valid reset tokens in the URL
  useEffect(() => {
    let isMounted = true;

    const clearRecoveryParams = () => {
      if (window.location.hash) {
        window.history.replaceState(
          {},
          document.title,
          `${window.location.pathname}${window.location.search}`
        );
      }
    };

    const initializeRecoverySession = async () => {
      setError("");
      const hashParams = new URLSearchParams(
        (window.location.hash || "").replace(/^#/, "")
      );
      const getParam = (name) => hashParams.get(name) || searchParams.get(name);
      const type = getParam("type");
      const normalizedType = (type || "").toLowerCase();
      const isRecoveryType = normalizedType === "recovery";
      const accessToken = getParam("access_token");
      const refreshToken = getParam("refresh_token");
      const code = getParam("code");

      try {
        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) throw error;
          clearRecoveryParams();
          requirePasswordReset();
          return;
        }

        if (isRecoveryType && accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) throw error;
          clearRecoveryParams();
          requirePasswordReset();
          return;
        }

        throw new Error(
          "Invalid or expired reset link. Please request a new password reset."
        );
      } catch (sessionError) {
        if (!isMounted) return;
        setError(
          sessionError.message ||
            "Invalid or expired reset link. Please request a new password reset."
        );
      }
    };

    initializeRecoverySession();

    return () => {
      isMounted = false;
    };
  }, [location, searchParams, requirePasswordReset]);

  const validateForm = () => {
    const errors = {};

    if (!formData.password) {
      errors.password = "Password is required";
    } else if (formData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    return errors;
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear specific field error when user starts typing
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setLoading(true);

    try {
      // Update the user's password
      const { error } = await supabase.auth.updateUser({
        password: formData.password,
      });

      if (error) {
        throw error;
      }

      setSuccess(true);
      clearPasswordResetRequirement();

      // Redirect to login after a short delay
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (error) {
      console.error("Password reset error:", error);
      setError(error.message || "Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, text: "" };

    let strength = 0;
    if (password.length >= 6) strength++;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    const strengthText = [
      "Very Weak",
      "Weak",
      "Fair",
      "Good",
      "Strong",
      "Very Strong",
    ];
    const strengthColor = [
      "error",
      "error",
      "warning",
      "info",
      "success",
      "success",
    ];

    return {
      strength: Math.min(strength, 5),
      text: strengthText[Math.min(strength, 5)],
      color: strengthColor[Math.min(strength, 5)],
    };
  };

  const passwordStrength = getPasswordStrength(formData.password);

  if (success) {
    return (
      <AuthLayout>
        <AuthCard
          icon={<CheckCircle sx={{ fontSize: 56, color: "success.main" }} />}
          title="Password updated"
          subtitle="You can now sign in with your new password. Taking you to the sign-in page..."
        >
          <Button variant="contained" fullWidth size="large" onClick={() => navigate("/login")}>
            Go to sign in
          </Button>
        </AuthCard>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <AuthCard title="Choose a new password" subtitle="Use at least 8 characters. A mix of letters, numbers and symbols is strongest.">
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ display: "grid", gap: 2 }}>
          <Box>
            <TextField
              fullWidth
              label="New password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleInputChange}
              error={!!formErrors.password}
              helperText={formErrors.password}
              required
              autoFocus
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {formData.password && (
              <Box sx={{ mt: 1 }} aria-live="polite">
                <LinearProgress
                  variant="determinate"
                  value={(passwordStrength.strength / 5) * 100}
                  color={passwordStrength.color}
                  aria-label="Password strength"
                  sx={{ height: 4, borderRadius: 2 }}
                />
                <Typography variant="caption" color="text.secondary">
                  Password strength: {passwordStrength.text}
                </Typography>
              </Box>
            )}
          </Box>

          <TextField
            fullWidth
            label="Confirm new password"
            name="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            value={formData.confirmPassword}
            onChange={handleInputChange}
            error={!!formErrors.confirmPassword}
            helperText={formErrors.confirmPassword}
            required
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  >
                    {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Lock />}
            sx={{ minHeight: 44 }}
          >
            {loading ? "Updating password..." : "Update password"}
          </Button>

          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
            Remembered it?{" "}
            <Link component="button" type="button" variant="body2" underline="hover" onClick={() => navigate("/login")} sx={{ fontWeight: 600, verticalAlign: "baseline" }}>
              Back to sign in
            </Link>
          </Typography>
        </Box>
      </AuthCard>
    </AuthLayout>
  );
}

export default ResetPassword;
